// packages/web/src/lib/fieldDisplay.ts — shared read-view formatting
// (ported 2026-07-29 from the Rexx fork's 2026-07-21 UI rework: every screen
// shows dense read-only info by default, one Edit button expands the full
// form — see RecordForm.tsx's own assist/edit machinery for the write side).
// One place for "how does a field value read as text" so any screen formats
// currency/dates/picklists identically.
import type { SchemaField } from './api';

/** The display currency, in one place — a tenant-level lever replaces this
 * constant when currency becomes configurable. */
export const DISPLAY_CURRENCY = 'CAD';

export function fmtMoney(n: number): string {
  return n.toLocaleString('en-CA', {
    style: 'currency',
    currency: DISPLAY_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Turns a picklist raw value ('screen_test') into a readable option label. */
export function humanizeValue(v: string): string {
  return v
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function fmtDate(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDatetime(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** A field counts as "no data" (skip it on a read view — don't display what
 * we don't have) only when it's genuinely empty: null, undefined, or ''. A
 * stored `false`/`0` is real data, not a gap. */
export function isFieldEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** Renders a field's value the way a read-only screen shows it — plain text,
 * no input chrome. Booleans render as Yes/No (never a raw `true`/`false`). */
export function formatFieldValue(field: SchemaField, value: unknown): string {
  if (isFieldEmpty(value)) return '';
  switch (field.type) {
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'currency':
      return fmtMoney(Number(value));
    case 'number':
      return String(value);
    case 'date':
      return fmtDate(String(value));
    case 'datetime':
      return fmtDatetime(String(value));
    case 'picklist':
      return humanizeValue(String(value));
    case 'duration': {
      const secs = Number(value);
      if (!Number.isFinite(secs)) return String(value);
      const h = Math.floor(secs / 3600);
      const m = Math.round((secs % 3600) / 60);
      return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ''}` : `${m}m`;
    }
    default:
      return String(value);
  }
}
